/**
 * ClickableZoneOverlay — zone-selection overlay.
 *
 * Renders a labeled grid of clickable zones over the paused video.
 * If the marker has a `correct_zone` field, shows correct/incorrect
 * feedback before dismissing.
 *
 * Props:
 *   question     — string instruction text from the marker
 *   correctZone  — string (optional) correct zone label, e.g. "B"
 *   markerId     — string marker ID for event context
 *   sceneId      — string scene ID for event context
 *   domain       — string domain for event context
 *   gridSize     — 'small' (2×2, zones 1-4) | 'large' (3×3, zones A-I)  default 'large'
 *   onDismiss    — () => void
 */

import React, { useRef, useState } from 'react';
import { emitLearnerEvent } from '../../services/learnerEvents';

const GRID_3X3 = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];
const GRID_2X2 = ['1', '2', '3', '4'];

export default function ClickableZoneOverlay({
  question,
  correctZone,
  markerId,
  sceneId,
  domain,
  gridSize = 'large',
  onDismiss,
}) {
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'incorrect' | null
  const mountTimeRef = useRef(Date.now());

  const zones = gridSize === 'small' ? GRID_2X2 : GRID_3X3;
  const cols = gridSize === 'small' ? 2 : 3;

  const handleZoneClick = async (zone) => {
    if (selected !== null) return; // already answered
    setSelected(zone);

    const hasCorrect = correctZone != null && correctZone !== '';
    const isCorrect = hasCorrect ? zone === String(correctZone) : null;
    if (hasCorrect) setFeedback(isCorrect ? 'correct' : 'incorrect');

    await emitLearnerEvent({
      event_type: 'overlay_answered',
      overlay_type: 'clickable_zone',
      marker_id: markerId,
      selected_zone: zone,
      is_correct: isCorrect,
      scene_id: sceneId,
      domain,
      time_to_answer_ms: Date.now() - mountTimeRef.current,
    });

    // Dismiss after short feedback delay if no correct_zone, or after feedback shown
    if (!hasCorrect) {
      if (onDismiss) onDismiss();
    } else {
      setTimeout(() => { if (onDismiss) onDismiss(); }, 1200);
    }
  };

  const handleSkip = async () => {
    await emitLearnerEvent({
      event_type: 'overlay_skipped',
      overlay_type: 'clickable_zone',
      marker_id: markerId,
      scene_id: sceneId,
      domain,
      time_to_answer_ms: Date.now() - mountTimeRef.current,
    });
    if (onDismiss) onDismiss();
  };

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span style={labelStyle}>Click the Zone</span>
          {!selected && (
            <button onClick={handleSkip} style={skipBtnStyle} title="Skip this question">
              Skip
            </button>
          )}
        </div>

        <p style={promptStyle}>{question || 'Click the correct area.'}</p>

        <div style={{ ...gridStyle, gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {zones.map((zone) => {
            let bg = '#0f172a';
            let border = '#334155';
            let color = '#94a3b8';

            if (selected === zone) {
              if (feedback === 'correct') { bg = 'rgba(34,197,94,0.15)'; border = '#22c55e'; color = '#22c55e'; }
              else if (feedback === 'incorrect') { bg = 'rgba(239,68,68,0.15)'; border = '#ef4444'; color = '#ef4444'; }
              else { bg = 'rgba(59,130,246,0.15)'; border = '#3b82f6'; color = '#3b82f6'; }
            } else if (feedback && correctZone && zone === String(correctZone)) {
              bg = 'rgba(34,197,94,0.1)'; border = '#22c55e'; color = '#22c55e';
            }

            return (
              <button
                key={zone}
                onClick={() => handleZoneClick(zone)}
                disabled={selected !== null}
                style={{ ...zoneBtnStyle, background: bg, borderColor: border, color }}
              >
                {zone}
              </button>
            );
          })}
        </div>

        {feedback && (
          <div style={{
            marginTop: 14,
            padding: '8px 12px',
            borderRadius: 6,
            background: feedback === 'correct' ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
            borderLeft: `3px solid ${feedback === 'correct' ? '#22c55e' : '#ef4444'}`,
            color: '#e2e8f0',
            fontSize: 13,
          }}>
            {feedback === 'correct'
              ? 'Correct! Well spotted.'
              : `Incorrect. The correct zone is ${correctZone}.`}
          </div>
        )}
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(0,0,0,0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
  backdropFilter: 'blur(4px)',
};

const cardStyle = {
  background: '#1e293b',
  borderRadius: 12,
  padding: '24px 28px',
  maxWidth: 560,
  width: '90%',
  border: '1px solid #334155',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 14,
};

const labelStyle = {
  fontSize: 11,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: '#94a3b8',
};

const skipBtnStyle = {
  background: 'none',
  border: '1px solid #334155',
  borderRadius: 4,
  color: '#64748b',
  fontSize: 11,
  cursor: 'pointer',
  padding: '2px 8px',
};

const promptStyle = {
  fontSize: 17,
  fontWeight: 600,
  color: '#f8fafc',
  margin: '0 0 18px',
  lineHeight: 1.5,
};

const gridStyle = {
  display: 'grid',
  gap: 10,
};

const zoneBtnStyle = {
  aspectRatio: '1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  fontWeight: 700,
  border: '2px solid',
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};
